import { Link, useParams } from '@tanstack/react-router';
import { HistoryIcon, ImageIcon, ReceiptTextIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DetailShell, DetailSkeleton, FactList, OwnerCard } from '@/components/detail-shell';
import { StatusBadge } from '@/components/status-badge';
import { EmptyState, ErrorNotice } from '@/components/states';
import { useTicketDetail } from '@/lib/admin-queries';
import { formatTashkent } from '@/lib/time';
import { strings } from '@/i18n/strings';

const DASH = '—';

/**
 * Bitta bilet — uni keltirgan skan va u ustida bajarilgan amallar bilan.
 *
 * Amallar tarixi (audit) sahifaning YARIMI, bezak emas. Bu sahifani `fraud_review` navbatidan
 * kelgan moderator ochadi, va uning birinchi savoli ko'pincha "bu bilet bilan ilgari nima
 * qilingan" bo'ladi. Javobsiz u qarorni ikkinchi marta takrorlashi mumkin — masalan boshqa
 * xodim allaqachon tiklab qo'ygan biletni yana tiklashi.
 */
export function TicketDetailPage() {
  const { ticketId } = useParams({ from: '/shell/moderation/tickets/$ticketId' });
  const detail = useTicketDetail(ticketId);

  const data = detail.data;

  return (
    <DetailShell
      title={data ? data.ticket.code : strings.detail.ticketTitle}
      description={strings.detail.ticketSubtitle}
      backTo="/moderation"
    >
      {detail.isError ? <ErrorNotice error={detail.error} /> : null}
      {detail.isPending ? <DetailSkeleton /> : null}

      {data ? (
        <>
          <OwnerCard owner={data.owner} />

          <Card>
            <CardHeader>
              <CardTitle>{strings.detail.ticketTitle}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge>{data.ticket.status}</StatusBadge>
                <StatusBadge>{data.ticket.source}</StatusBadge>
              </div>

              <FactList
                facts={[
                  [strings.detail.ticketAcquired, data.ticket.acquiredDate],
                  [strings.detail.ticketMonth, data.ticket.monthKey],
                  [strings.draws.columnScheduledAt, data.ticket.dailyDrawDate ?? DASH],
                  [
                    strings.detail.ticketVerifiedAt,
                    data.ticket.verifiedAt ? formatTashkent(data.ticket.verifiedAt) : DASH,
                  ],
                  [strings.detail.ticketWonDraw, data.ticket.wonDrawId ?? DASH],
                  [strings.moderation.columnTotal, data.ticket.receiptTotal ?? DASH],
                  [strings.moderation.columnVerifiedBy, data.ticket.receiptTotalVerifiedBy ?? DASH],
                ]}
              />

              {/* Biletni KELTIRGAN skanga o'tish — qarordan oldingi oxirgi qadam. */}
              <div className="flex flex-wrap gap-2">
                {data.receipt ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link
                      to="/moderation/receipts/$receiptId"
                      params={{ receiptId: data.receipt.id }}
                    >
                      <ReceiptTextIcon aria-hidden="true" />
                      {strings.detail.sourceReceipt}
                    </Link>
                  </Button>
                ) : null}
                {data.photo ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link
                      to="/moderation/photos/$photoScanId"
                      params={{ photoScanId: data.photo.id }}
                    >
                      <ImageIcon aria-hidden="true" />
                      {strings.detail.sourcePhoto}
                    </Link>
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{strings.detail.auditHeading}</CardTitle>
            </CardHeader>
            <CardContent>
              {data.audit.length === 0 ? (
                <EmptyState title={strings.detail.auditEmpty} icon={HistoryIcon} />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{strings.detail.auditWhen}</TableHead>
                      <TableHead>{strings.detail.auditAdmin}</TableHead>
                      <TableHead>{strings.detail.auditAction}</TableHead>
                      <TableHead>{strings.detail.auditDetails}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.audit.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="tabular whitespace-nowrap">
                          {formatTashkent(entry.createdAt)}
                        </TableCell>
                        <TableCell>{entry.adminEmail}</TableCell>
                        <TableCell className="font-mono text-xs">{entry.action}</TableCell>
                        {/*
                          `metadata` — xom JSON. Uni "chiroyli" ko'rsatishga urinish sabab
                          matnini kesib qo'yardi, va aynan u qaror uchun eng kerakli maydon.
                        */}
                        <TableCell className="text-muted-foreground max-w-96 text-xs break-words">
                          {JSON.stringify(entry.metadata)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </DetailShell>
  );
}
